import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TableSort from '../tableComponents/TableSort'
import Help from '../Help'
import { monthabbs, variableName, smryName } from "../js/constants"
import { formatDateRange, formatHourFilter, hasMissing, roundFixValue } from "../js/utilityRoutines"

export default function MonsmryTableWx(props) { 
  const { wxCodeData, totobscnt, totmisscnt, months_to_use, userParams, stnName } = props.results;
  const allCodesList = Object.keys(wxCodeData);
  const [ dataForTable, setDataForTable ] = useState(null);
  const [ tableBottom, setTableBottom ] = useState([]);
  const [ wxCodesForTitle, setWxCodesForTitle ] = useState(userParams.wxvariables);
  const [ viewCodeHelp, setViewCodeHelp ] = useState(false);
  // initialize all checkboxes to true
  const [ checkboxStatus, setCheckboxStatus ] = useState(() => {
    const newStatus = {};
    if (allCodesList && Array.isArray(allCodesList)) {
      allCodesList.forEach(key => {
        newStatus[key] = true;
      });
    }
    return newStatus;
  });
  // initialize table header rows
  const [ heads, setHeads ] = useState(() => {
    const headRow = [];
    headRow.push({text: "", id: "index", invisible: true, initSort: true, sortOrder: "asc"});
    headRow.push({text: "Year", id: "year", altSort: "index"});
    months_to_use.map((m) => {
        headRow.push({text: m <= 11 ? monthabbs[m] : monthabbs[m-12], id: m});
    })
    return headRow;
  });
  const totMiss = totmisscnt.toLocaleString("en");
  const totObs = totobscnt.toLocaleString("en");
  const dateRange = "<br />" + formatDateRange(userParams);
  const hourFilter = "<br />" + formatHourFilter(userParams);
  const hasM = hasMissing(dataForTable);
  const titleText = smryName[userParams.smry] + " " + variableName[userParams.variable].name + 
    " includes " + (wxCodesForTitle.includes(",") ? "any of: " : "") + wxCodesForTitle +
    "<br/ >" + stnName;
  const footerText =  "Based on a total of "+totObs+" hourly observations; "+totMiss+" missing." +
    dateRange +
    hourFilter +
    hasM;

  // update status when a box is checked (see useEffect below)
  const handleCheckboxChange = (event) => {
    const name = event.target.name;
    setCheckboxStatus({...checkboxStatus, [name]: !checkboxStatus[name]})
  }

  // display a group of checkboxes
  const checkboxes = () => {
    return (
      <FormGroup row>
        { allCodesList.map((opt) => (
          <FormControlLabel   
            key={opt}
            control={
              <Checkbox 
                checked={checkboxStatus[opt]} 
                name={opt} 
                sx={{ py: 0, pl: 1, pr: "3px"}} 
                onChange={(e) => handleCheckboxChange(e)}
              />
            }
            label={<Typography sx={{ fontSize: "0.875rem"}}>{opt}</Typography>}
          />  
        ))}
      </FormGroup>
    )
  }

  // build/rebuild summary portion of table when table data changes
  useEffect(() => {
    if (dataForTable && dataForTable.length > 0) {
      let newMax = {}, newMin = {}, newSum = {}, newCnt = {}, bottomMeans = {};
      dataForTable.forEach((yrdata,i) => {
        Object.keys(yrdata).forEach(mn => {
          if (!['index','year','sx'].includes(mn)) {
            if (i === 0) {
              newSum[mn] = 0;
              newCnt[mn] = 0;
              newMax[mn] = -Number.MAX_VALUE;
              newMin[mn] = Number.MAX_VALUE;
            }
            if (!["M",""].includes(yrdata[mn])) {
              newSum[mn] += yrdata[mn]
              newCnt[mn] += 1
              newMax[mn] = Math.max(newMax[mn], yrdata[mn])
              newMin[mn] = Math.min(newMin[mn], yrdata[mn])
            }
          }
        })
      })
      Object.keys(newCnt).forEach((i) => {
        bottomMeans[i] = newCnt[i] > 0 ? roundFixValue(newSum[i] / newCnt[i], 1) : "";
      })
      setTableBottom([
        {...newMax, year: "Max", sx: {borderTop: "1.5pt solid black"}},
        {...bottomMeans, year: "Mean"},
        {...newMin, year: "Min"}
      ])
    } else {
    	setTableBottom([])
    }
  },[dataForTable])

  // rebuild table when checkboxes change
  useEffect(() => {
    // update table data
    const checkedCodes = Object.keys(checkboxStatus).filter(code => checkboxStatus[code] === true);
    if (checkedCodes.length > 0) {
      let tablesums = Array.from({ length: wxCodeData[checkedCodes[0]].length }, () => ({ }));
      checkedCodes.forEach(code => {
        wxCodeData[code].forEach((yrdata,i) => {
          Object.keys(yrdata).forEach(mn => {
            if (['index','year','sx'].includes(mn)) {
              tablesums[i][mn] = wxCodeData[code][i][mn]
            } else {
              if (!tablesums[i].hasOwnProperty(mn)) {
                tablesums[i][mn] = 0    // initialize
              }
              if (!isNaN(wxCodeData[code][i][mn]) && wxCodeData[code][i][mn] !== "") {
                  tablesums[i][mn] += wxCodeData[code][i][mn]
              } else if (wxCodeData[code][i][mn] === "M") {
                tablesums[i][mn] = "M"
              } else {
                tablesums[i][mn] = ""
              }
            }
          })
        })
      })
      setDataForTable([...tablesums])
      // update list of codes in title
      setWxCodesForTitle(checkedCodes.join(","))
    } else {
      setDataForTable([])
      setWxCodesForTitle("")
    }
  }, [checkboxStatus])

  return (
    <Box>
      {Object.keys(checkboxStatus).length > 0 &&
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">
            {"Present weather codes found were: "} 
          </Typography>
          {checkboxes()}
        </Box>
      }
      {Object.keys(checkboxStatus).length > 1 &&
        <>
          <Button 
            variant="outlined" 
            size="small"   
            sx={{ mb: 1 }}
            onClick={() => { setViewCodeHelp(!viewCodeHelp) }}
          >
            {viewCodeHelp ? "Hide" : "View"} present weather code definitions
          </Button>
          {viewCodeHelp &&
            <Help variable="wx" />
          }
        </>
      }
      {heads &&
      <>
        <TableSort 
          titleText={titleText} 
          heads={heads}  
          tabledata={dataForTable} 
          tableNoSortData={tableBottom} 
          footerText={footerText}
          />
        </>
      }
    </Box>
  )
}
